import { locales, type Locale } from "@aircap/common/models";
import { computed } from "vue";
import { useFirebaseAuth } from "vuefire";
import { useLocale } from "vuetify";
import { z } from "zod";
import { de as zodDe, en as zodEn, es as zodEs, fr as zodFr, nl as zodNl } from "zod/v4/locales";
import { useI18n } from "./useI18n";

const zodLocales = {
  fr: zodFr,
  en: zodEn,
  de: zodDe,
  nl: zodNl,
  es: zodEs,
};

export interface Language {
  code: Locale;
  name: string;
}

export function useLanguage() {
  const { locale: i18nLocale } = useI18n();
  const { current: vuetifyLocale } = useLocale();
  const auth = useFirebaseAuth();

  const localesName = {
    en: "English",
    fr: "Français",
    de: "Deutsch",
    nl: "Nederlands",
    es: "Español",
  } as const satisfies Record<Locale, string>;

  const changeLanguage = (langCode: Locale) => {
    localStorage.setItem("locale", langCode);
    i18nLocale.value = langCode;
    vuetifyLocale.value = langCode;
    z.config(zodLocales[langCode]());
    if (auth) auth.languageCode = langCode;
  };

  const currentLanguage = computed<Language>(() => {
    return { code: i18nLocale.value as Locale, name: localesName[i18nLocale.value as Locale] };
  });

  return {
    languages: locales.map((locale) => ({
      code: locale,
      name: localesName[locale],
    })) satisfies Language[],
    changeLanguage,
    currentLanguage,
  };
}
